import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { ArrowRight, Heart, Shield, Sparkles, MessageSquare, Filter, Crown } from 'lucide-react';
import { useEffect, useState } from 'react';

const LOGO_URL = "/logo-wordmark.png";

// Editorial couple photography for the marketing carousel. These are
// generic photorealistic stock-style images (generated via Nano Banana) used
// as visual anchors on the landing page. They are NOT customer testimonials.
// Do NOT re-attach quotes or "Met on Meet to Keep" attributions to these
// entries until real, verifiable users opt in to being featured.
const COUPLES = [
  { src: "/couple-1.jpg", alt: "Couple sharing a quiet moment together" },
  { src: "/couple-2.jpg", alt: "Two people laughing during a first date" },
  { src: "/couple-3.jpg", alt: "Partners on a weekend outing" },
  { src: "/couple-4.jpg", alt: "Couple enjoying a slow afternoon at home" },
];

// Once we have at least this many admin-approved opt-in testimonials, the
// landing carousel switches from decorative photos to real user stories
// (with a photo + display-name credit — no "Met on Meet to Keep" tagline).
// Below the threshold, we stay decorative to avoid a lonely single-quote
// look. Tunable without a backend deploy.
const REAL_TESTIMONIAL_MIN = 3;

function normalizePhoto(url) {
  if (!url) return null;
  if (url.startsWith('http') || url.startsWith('/')) return url;
  return `/api/files/${url}`;
}

export default function LandingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeCouple, setActiveCouple] = useState(0);
  const [testimonials, setTestimonials] = useState(null); // null=loading, array=loaded

  // Fetch approved testimonials once on mount. The endpoint is public and
  // very small; failure just leaves the carousel in decorative mode.
  useEffect(() => {
    const controller = new AbortController();
    fetch('/api/testimonials/public', { signal: controller.signal })
      .then((r) => r.ok ? r.json() : { items: [] })
      .then((d) => setTestimonials(Array.isArray(d?.items) ? d.items : []))
      .catch(() => setTestimonials([]));
    return () => controller.abort();
  }, []);

  // Prefer real testimonials once we have the threshold; otherwise use the
  // decorative stock couples so a single approved story doesn't feel lonely.
  const slides = (testimonials && testimonials.length >= REAL_TESTIMONIAL_MIN)
    ? testimonials.map((t) => ({
        src: normalizePhoto(t.photo_url) || COUPLES[0].src,
        alt: `${t.display_names} — Meet to Keep couple`,
        quote: t.quote,
        credit: t.display_names,
      }))
    : COUPLES.map((c) => ({ ...c, quote: null, credit: null }));

  useEffect(() => {
    const id = setInterval(() => setActiveCouple(i => (i + 1) % slides.length), 5500);
    return () => clearInterval(id);
  }, [slides.length]);

  useEffect(() => {
    if (user) navigate(user.profile_complete ? '/discover' : '/profile/setup', { replace: true });
  }, [user, navigate]);

  if (user) return null;

  return (
    <div className="min-h-screen bg-white text-[#1A1A1A] overflow-hidden" data-testid="landing-page">
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 px-6 py-4 flex items-center justify-between bg-white/90 backdrop-blur-xl border-b border-black/[0.04]">
        <img src={LOGO_URL} alt="Meet to Keep" className="h-28 w-auto object-contain" data-testid="brand-logo" />
        <button onClick={() => navigate('/auth')} data-testid="header-sign-in"
          className="text-sm font-medium text-zinc-500 hover:text-[#1A1A1A] px-4 py-2 rounded-full border border-black/[0.08] hover:border-black/[0.15] transition-all">
          Sign in
        </button>
      </nav>

      {/* Hero */}
      <section className="min-h-screen grid lg:grid-cols-[1.1fr_1fr] gap-12 items-center px-6 pt-24 pb-16 max-w-6xl mx-auto">
        {/* Copy */}
        <div className="text-left lg:pr-6">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#EF4444]/5 border border-[#EF4444]/10 mb-8 animate-fade-up">
            <Heart size={14} className="text-[#EF4444]" fill="currentColor" />
            <span className="text-xs font-medium text-[#EF4444] tracking-wide uppercase">Dating with intention</span>
          </div>

          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight leading-[1.05] mb-6 animate-fade-up" style={{animationDelay:'0.1s'}}>
            Swipe less.<br />
            <span className="brand-gradient-text">Connect more.</span>
          </h1>

          <p className="text-lg sm:text-xl text-zinc-500 max-w-xl mb-10 leading-relaxed animate-fade-up" style={{animationDelay:'0.2s'}}>
            The dating app that rewards intentional swiping with deeper connections. Compatibility scores, smart limits, and AI-powered conversations.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 animate-fade-up" style={{animationDelay:'0.3s'}}>
            <button onClick={() => navigate('/auth')} data-testid="get-started-btn"
              className="brand-gradient text-white px-10 py-4 rounded-full text-base font-semibold transition-all hover:scale-[1.02] brand-glow active:scale-95">
              Get Started Free <ArrowRight className="inline ml-2 w-4 h-4" />
            </button>
            <button onClick={() => document.getElementById('features')?.scrollIntoView({behavior:'smooth'})}
              className="px-10 py-4 rounded-full text-base font-medium border border-black/[0.08] hover:border-black/[0.15] hover:bg-black/[0.02] transition-all text-zinc-600">
              How it works
            </button>
          </div>

          {/* Mobile app — coming soon */}
          <div className="mt-10 animate-fade-up" style={{animationDelay:'0.4s'}} data-testid="mobile-apps-banner">
            <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-400 mb-3">
              Native app · coming soon
            </p>
            <div className="flex flex-wrap gap-3">
              {/* Apple App Store badge */}
              <div
                data-testid="app-store-badge"
                className="inline-flex items-center gap-3 px-4 py-2.5 rounded-xl bg-black text-white relative overflow-hidden group cursor-default select-none"
                aria-label="Download on the App Store — coming soon"
              >
                <svg viewBox="0 0 384 512" className="w-6 h-6" fill="currentColor" aria-hidden="true">
                  <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/>
                </svg>
                <div className="text-left">
                  <div className="text-[9px] uppercase tracking-wider text-white/70 leading-none">Download on the</div>
                  <div className="text-[15px] font-semibold leading-tight mt-0.5">App Store</div>
                </div>
                <span className="ml-1 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider bg-white/15 text-white/90 rounded-full">
                  Soon
                </span>
              </div>

              {/* Google Play badge */}
              <div
                data-testid="google-play-badge"
                className="inline-flex items-center gap-3 px-4 py-2.5 rounded-xl bg-black text-white relative overflow-hidden group cursor-default select-none"
                aria-label="Get it on Google Play — coming soon"
              >
                {/* Multicolored Play triangle — canonical Google Play mark
                    (blue left face, green top face, yellow right tip, red
                    bottom face). All 4 solid colours meeting at a single
                    interior pivot, matching the current 2026 Play badge. */}
                <svg viewBox="0 0 40 45" className="w-6 h-6" aria-hidden="true">
                  <path fill="#2196F3" d="M1 1.6C.4 2 0 2.6 0 3.4v38.2c0 .8.4 1.5 1 1.8l21.6-21.9L1 1.6z"/>
                  <path fill="#34A853" d="M28.8 15.4L3.5.5C2.9.2 2.2.2 1.7.4L22.6 21.5l6.2-6.1z"/>
                  <path fill="#EA4335" d="M28.8 29.6L22.6 23.5 1.7 44.6c.5.2 1.2.2 1.8-.1l25.3-14.9z"/>
                  <path fill="#FBBC04" d="M37.3 20.6l-8.5-5.2-6.2 6.1 6.2 6.1 8.5-5.2c1.7-1 1.7-2.8 0-1.8z"/>
                </svg>
                <div className="text-left">
                  <div className="text-[9px] uppercase tracking-wider text-white/70 leading-none">Get it on</div>
                  <div className="text-[15px] font-semibold leading-tight mt-0.5">Google Play</div>
                </div>
                <span className="ml-1 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider bg-white/15 text-white/90 rounded-full">
                  Soon
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Editorial couple carousel — rotating real couples, auto-cycle every 5.5s */}
        <div className="relative animate-fade-up" style={{animationDelay:'0.4s'}}>
          <div className="relative aspect-[5/4] rounded-[32px] overflow-hidden bg-zinc-100 shadow-2xl" data-testid="couples-carousel">
            {slides.map((c, i) => (
              <div key={i}
                className={`absolute inset-0 transition-opacity duration-700 ease-out ${i === activeCouple ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
                aria-hidden={i !== activeCouple}>
                <img src={c.src} alt={c.alt} className="w-full h-full object-cover" loading={i === 0 ? 'eager' : 'lazy'} />
                {c.quote && (
                  <>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/15 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-7">
                      <p className="font-display italic text-white text-base sm:text-lg leading-snug drop-shadow-md max-w-md" data-testid={`couple-quote-${i}`}>
                        &ldquo;{c.quote}&rdquo;
                      </p>
                      <p className="text-white/85 text-[12px] mt-2 tracking-wide font-medium" data-testid={`couple-credit-${i}`}>— {c.credit}</p>
                    </div>
                  </>
                )}
              </div>
            ))}

            {/* Dot indicators */}
            <div className="absolute top-5 right-5 flex gap-1.5 z-10">
              {slides.map((_, i) => (
                <button key={i} onClick={() => setActiveCouple(i)}
                  data-testid={`couple-dot-${i}`}
                  aria-label={`Show couple ${i + 1}`}
                  className={`h-1.5 rounded-full transition-all ${i === activeCouple ? 'w-8 bg-white' : 'w-1.5 bg-white/50 hover:bg-white/75'}`}
                />
              ))}
            </div>
          </div>
          <div className="absolute -left-10 -bottom-10 w-40 h-40 rounded-full bg-[#EF4444] opacity-10 blur-3xl pointer-events-none" />
          <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-[#3B82F6] opacity-10 blur-3xl pointer-events-none" />
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-32 px-6 bg-[#FAFAFA]">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4">Why <span className="brand-gradient-text">Meet to Keep</span></h2>
            <p className="text-zinc-500 max-w-lg mx-auto">Every feature designed to help you find someone worth keeping.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { icon: Heart, title: 'Compatibility Score', desc: 'Matched on values, lifestyle, and goals.', color: '#EF4444' },
              { icon: Shield, title: 'Smart Limits', desc: "No spam swiping. Balance for everyone.", color: '#3B82F6' },
              { icon: Sparkles, title: 'AI Icebreakers', desc: 'Personalized conversation starters.', color: '#8B5CF6' },
              { icon: Filter, title: 'Advanced Filters', desc: 'Filter by age, distance, answers.', color: '#22C55E' },
              { icon: Crown, title: 'Premium Tiers', desc: 'Unlimited likes, read receipts, boosts.', color: '#F59E0B' },
              { icon: MessageSquare, title: 'Real-Time Chat', desc: 'Typing indicators and read receipts.', color: '#3B82F6' },
            ].map((f, i) => (
              <div key={f.title} className="p-6 rounded-2xl bg-white border border-black/[0.04] hover:shadow-lg hover:border-black/[0.08] transition-all group" data-testid={`feature-card-${i}`}>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4" style={{background:`${f.color}10`}}>
                  <f.icon size={20} style={{color:f.color}} />
                </div>
                <h3 className="font-semibold mb-1.5">{f.title}</h3>
                <p className="text-sm text-zinc-500 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-32 px-6">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">Ready to date <span className="brand-gradient-text">with purpose?</span></h2>
          <p className="text-zinc-500 mb-10">Join intentional daters who are done swiping aimlessly.</p>
          <button onClick={() => navigate('/auth')} data-testid="cta-get-started-btn"
            className="brand-gradient text-white px-12 py-4 rounded-full text-base font-semibold transition-all hover:scale-[1.02] brand-glow active:scale-95">
            Get Started Free <ArrowRight className="inline ml-2 w-4 h-4" />
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 px-6 border-t border-black/[0.04]" data-testid="landing-footer">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-6">
            <img src={LOGO_URL} alt="Meet to Keep" className="h-5" />
            <div className="flex gap-6">
              <button onClick={() => navigate('/privacy')} className="text-xs text-zinc-500 hover:text-zinc-900 transition-colors" data-testid="footer-privacy">Privacy Policy</button>
              <button onClick={() => navigate('/terms')} className="text-xs text-zinc-500 hover:text-zinc-900 transition-colors" data-testid="footer-terms">Terms of Service</button>
              <a href="/press" onClick={(e) => { e.preventDefault(); navigate('/press'); }} className="text-xs text-zinc-500 hover:text-zinc-900 transition-colors" data-testid="footer-press">Press &amp; Media</a>
              <a href="/articles" onClick={(e) => { e.preventDefault(); navigate('/articles'); }} className="text-xs text-zinc-500 hover:text-zinc-900 transition-colors" data-testid="footer-articles">Dating Advice</a>
              <a href="mailto:hello@meettokeep.com" className="text-xs text-zinc-500 hover:text-zinc-900 transition-colors" data-testid="footer-contact">Contact</a>
            </div>
          </div>
          {/* Crawlable identity sentence — establishes Meet to Keep as a dating app founded by
              Manuel Santana in server-rendered HTML for search engines. Visible in the footer so
              it never reads as hidden SEO text. */}
          <p className="text-[11px] leading-relaxed text-zinc-500 mb-5 max-w-3xl" data-testid="landing-identity-statement">
            Meet to Keep is an intentional dating app founded by{' '}
            <a href="https://iammanuelsantana.com/" rel="author" className="underline hover:text-zinc-800">Manuel Santana</a>,
            {' '}built for singles seeking meaningful, long-term relationships through
            compatibility-based matching and intentional dating features.
          </p>
          <div className="pt-5 border-t border-black/[0.04] flex flex-col sm:flex-row items-center justify-between gap-2" data-testid="footer-copyright">
            <p className="text-[11px] text-zinc-400">© {new Date().getFullYear()} Meet to Keep LLC. All rights reserved.</p>
            <p className="text-[11px] text-zinc-400">Dating with intention. Made for real connections.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
